import type { FormField } from '@lucid/lego';
import { validationPatterns } from '@lucid/lego';
import { escapeHtml, getPostalCodePattern, utcToZonedTimeSlot } from '../../../utils/tools';
import type {
  TestDriveSlotDetail,
  TestDriveVehicle,
} from '../../../api/services/lupp/test-drive/dto.test-drive';
import { TEST_DRIVE_SLOT_AVAILABLE } from '../../../api/services/lupp/test-drive/dto.test-drive';
import {
  carMakerOptions,
  getEmailConsentOptions,
  getFinanceTypeOptions,
  getPhoneConsentOptions,
  getPreferredContactMethodOptions,
  getPurchaseTimingOptions,
  getPurchaseTypeOptions,
  getStatusOptions,
  getTitleOptions,
  getInterestedModelOptions,
} from '../constants';

type CreateLeadFormFieldsOptions = {
  onCancel?: () => void;
  location?: string;
  organizationName?: string;
  testDriveVehicles?: TestDriveVehicle[];
  availableSlots?: TestDriveSlotDetail[];
  selection?: { modelTrim?: string; date?: string };
  isTestDrive?: boolean;
  countryCode?: string;
};

const buildModelTrimOptions = (vehicles?: TestDriveVehicle[]) =>
  vehicles?.map((vehicle) => ({ label: vehicle.description, value: vehicle.description })) ?? [];

const buildTimeOptions = (slots?: TestDriveSlotDetail[]) =>
  slots
    ?.filter(
      (slot) => slot.slotAvailable && slot.slotAvailabilityStatus === TEST_DRIVE_SLOT_AVAILABLE
    )
    .map((slot) => {
      const value = utcToZonedTimeSlot(slot.startTime, slot.endTime, slot.timezone);
      return { label: value.replace('-', ' - '), value };
    })
    .filter((option) => option.value) ?? [];

/**
 * True only once a vehicle and date are chosen and the slots call has answered, so the
 * message never flashes while the dropdown is still legitimately empty.
 */
const hasNoBookableSlots = (options?: CreateLeadFormFieldsOptions): boolean =>
  Boolean(options?.selection?.modelTrim) &&
  Boolean(options?.selection?.date) &&
  Boolean(options?.availableSlots) &&
  !buildTimeOptions(options?.availableSlots).length;

/** Undefined while the call is in flight, so an empty list only counts once it has answered. */
const hasNoVehicles = (options?: CreateLeadFormFieldsOptions): boolean =>
  Array.isArray(options?.testDriveVehicles) && options.testDriveVehicles.length === 0;

// A test drive that can't be booked would fail on submit, so block it up front.
const isSubmitBlocked = (options?: CreateLeadFormFieldsOptions): boolean =>
  Boolean(options?.isTestDrive) && (hasNoVehicles(options) || hasNoBookableSlots(options));

export const createLeadFormFields = (
  options?: CreateLeadFormFieldsOptions,
  labels?: Record<string, string>,
  locale?: string
): FormField[] => [
  // Personal Information Section
  {
    id: 'informationGroup',
    fieldType: 'group',
    title: '',
    width: 'full',
    children: [
      {
        id: 'leadFirstName',
        fieldType: 'text',
        name: 'prospectFirstName',
        apiKey: 'prospectFirstName',
        label: labels?.['form.firstName'] ?? 'First Name',
        placeholder: labels?.['form.firstNamePlaceholder'] ?? 'Enter First Name',
        required: true,
        width: 'quarter',
        validationMessages: {
          required: labels?.['form.firstNameRequired'] ?? 'First name is required',
        },
      },
      {
        id: 'leadMiddleName',
        fieldType: 'text',
        name: 'middleName',
        apiKey: 'prospectMiddleName',
        label: labels?.['form.middleName'] ?? 'Middle Name',
        width: 'quarter',
        placeholder: labels?.['form.middleNamePlaceholder'] ?? 'Enter Middle Name',
      },
      {
        id: 'leadLastName',
        fieldType: 'text',
        name: 'prospectLastName',
        apiKey: 'prospectLastName',
        label: labels?.['form.lastName'] ?? 'Last Name',
        placeholder: labels?.['form.lastNamePlaceholder'] ?? 'Enter Last Name',
        required: true,
        width: 'quarter',
        validationMessages: {
          required: labels?.['form.lastNameRequired'] ?? 'Last name is required',
        },
      },
      {
        id: 'title',
        fieldType: 'dropdown',
        name: 'title',
        apiKey: 'prospectTitle',
        label: labels?.['form.title'] ?? 'Title',
        placeholder: labels?.['form.select'] ?? 'Select',
        required: true,
        width: 'quarter',
        options: getTitleOptions(labels, locale),
        validationMessages: {
          required: labels?.['form.titleRequired'] ?? 'Title is required',
        },
      },
      {
        id: 'leadEmail',
        fieldType: 'email',
        name: 'email',
        apiKey: 'prospectEmail',
        label: labels?.['form.email'] ?? 'Email',
        placeholder: labels?.['form.customerexamplecom'] ?? 'customer@example.com',
        required: true,
        width: 'half',
        validationMessages: {
          required: labels?.['form.emailRequired'] ?? 'Email is required',
          pattern: labels?.['form.emailAddressInvalid'] ?? 'Please enter a valid email address',
        },
      },
      {
        id: 'leadPhone',
        fieldType: 'phone',
        name: 'prospectPhone',
        apiKey: 'prospectPhone',
        label: labels?.['form.phone'] ?? 'Phone',
        required: false,
        width: 'half',
        validationMessages: {
          pattern: labels?.['form.phoneNumberInvalid'] ?? 'Please enter a valid phone number',
        },
      },
      {
        id: 'zipCode',
        fieldType: 'text',
        name: 'zipCode',
        apiKey: 'zipCode',
        label: labels?.['form.postalCode'] ?? 'Postal Code',
        placeholder: labels?.['form.postalCodePlaceholder'] ?? 'Enter Postal Code',
        required: true,
        width: 'third',
        pattern: getPostalCodePattern(options?.countryCode),
        postalCodeAutoPopulate: {
          cityField: 'city',
          countryField: 'country',
          regionField: 'stateProvince',
        },
        validationMessages: {
          required: labels?.['form.postalCodeRequired'] ?? 'Postal code is required',
          pattern:
            labels?.['form.postalCodeInvalid'] ?? 'Please enter a valid postal code (e.g. 1234 AB)',
        },
      },
      {
        id: 'stateProvince',
        fieldType: 'text',
        name: 'stateProvince',
        apiKey: 'stateProvince',
        visible: false,
      },
      {
        id: 'city',
        fieldType: 'text',
        name: 'city',
        apiKey: 'city',
        label: labels?.['form.city'] ?? 'City',
        placeholder: labels?.['form.cityPlaceholder'] ?? 'Enter City',
        required: true,
        width: 'third',
        pattern: validationPatterns.city,
        validationMessages: {
          required: labels?.['form.cityRequired'] ?? 'City is required',
          pattern: labels?.['form.cityNameInvalid'] ?? 'Please enter a valid city name',
        },
      },
      {
        id: 'country',
        fieldType: 'text',
        name: 'country',
        apiKey: 'countryCode',
        label: labels?.['form.country'] ?? 'Country',
        placeholder: '',
        disabled: true,
        width: 'third',
      },
    ],
  },
  // Purchase Profile Section
  {
    id: 'interestedModelTestDrive',
    fieldType: 'dropdown',
    name: 'interestedModelTestDrive',
    apiKey: 'interestedTrim',
    label: labels?.['form.interestedModeltrim'] ?? 'Interested Model/Trim',
    placeholder: labels?.['form.aModelPlaceholder'] ?? 'Select a Model',
    options: getInterestedModelOptions(),
  },
  {
    id: 'interestedVehicleCondition',
    fieldType: 'radio_group',
    name: 'interestedVehicleCondition',
    apiKey: 'interestedVehicleCondition',
    label: labels?.['form.interestedVehicleCondition'] ?? 'Interested Vehicle Condition',
    width: 'full',
    layout: 'horizontal',
    options: [
      { label: labels?.['form.newVehicle'] ?? 'New Vehicle', value: 'new', default: true },
      { label: labels?.['form.usedVehicle'] ?? 'Used Vehicle', value: 'used' },
    ],
  },
  {
    id: 'currentCarMake',
    fieldType: 'dropdown',
    name: 'currentCarMake',
    apiKey: 'currentCarMake',
    label: labels?.['form.makeOfCurrent'] ?? 'Make of Current Car',
    placeholder: labels?.['form.makePlaceholder'] ?? 'Select Make',
    width: 'full',
    options: carMakerOptions,
  },
  {
    id: 'purchaseType',
    fieldType: 'dropdown',
    name: 'purchaseType',
    apiKey: 'purchaseType',
    label: labels?.['form.purchaseType'] ?? 'Purchase Type',
    placeholder: labels?.['form.purchaseTypePlaceholder'] ?? 'Select Purchase Type',
    width: 'full',
    options: getPurchaseTypeOptions(labels),
  },
  {
    id: 'financeType',
    fieldType: 'dropdown',
    name: 'financeType',
    apiKey: 'financeType',
    label: labels?.['form.finance'] ?? 'Finance',
    placeholder: labels?.['form.financeTypePlaceholder'] ?? 'Select Finance Type',
    width: 'full',
    options: getFinanceTypeOptions(labels),
  },
  {
    id: 'purchaseTiming',
    fieldType: 'dropdown',
    name: 'purchaseTiming',
    apiKey: 'purchaseTiming',
    label: labels?.['form.timing'] ?? 'Timing',
    placeholder: labels?.['form.timingPlaceholder'] ?? 'Select Timing',
    required: true,
    width: 'full',
    validationMessages: {
      required: labels?.['form.timingRequired'] ?? 'Timing is required',
    },
    options: getPurchaseTimingOptions(labels),
  },
  {
    id: 'preferredContactMethod',
    fieldType: 'dropdown',
    name: 'preferredContactMethod',
    apiKey: 'preferredContactMethod',
    label: labels?.['form.preferredContactMethod'] ?? 'Preferred Contact Method',
    placeholder:
      labels?.['form.preferredContactMethodPlaceholder'] ?? 'Select Preferred Contact Method',
    width: 'full',
    options: getPreferredContactMethodOptions(labels),
  },
  // Consent Section
  {
    id: 'isEmailOptIn',
    fieldType: 'checkbox_group',
    name: 'isEmailOptIn',
    apiKey: 'isEmailOptIn',
    label: labels?.['form.emailConsent'] ?? 'Email Consent',
    width: 'full',
    layout: 'horizontal',
    options: getEmailConsentOptions(labels),
  },
  {
    id: 'isCallOptIn',
    fieldType: 'radio_group',
    name: 'callConsent',
    apiKey: 'callConsent',
    label: labels?.['form.callConsent'] ?? 'Call Consent',
    width: 'full',
    layout: 'horizontal',
    options: getPhoneConsentOptions(labels),
    disabledDependency: { name: 'prospectPhone', negate: true },
  },
  // Schedule Test Drive Section
  {
    id: 'testDriveSection',
    fieldType: 'group',
    width: 'full',
    title: labels?.['form.scheduleTestDrive'] ?? 'Schedule Test Drive',
    children: [
      {
        id: 'isTestDrive',
        fieldType: 'checkbox_group',
        name: 'isTestDrive',
        apiKey: 'isTestDrive',
        label: labels?.['form.testDriveNow'] ?? 'Test Drive Now',
        width: 'half',
        layout: 'horizontal',
        defaultValue: ['yes'],
        options: [{ label: labels?.['form.yes'] ?? 'Yes', value: 'yes' }],
      },
      {
        id: 'appointmentLocationName',
        fieldType: 'content',
        label: labels?.['form.location'] ?? 'Location',
        width: 'half',
        content: options?.location || '-',
      },
      {
        id: 'selectedModelTrim',
        fieldType: 'dropdown',
        name: 'selectedModelTrim',
        apiKey: 'selectedModelTrim',
        label: labels?.['form.selectedModeltrim'] ?? 'Selected Model/Trim',
        placeholder: labels?.['form.aModelPlaceholder'] ?? 'Select a Model',
        width: 'full',
        visible: !hasNoVehicles(options),
        dependency: { name: 'isTestDrive', value: 'yes' },
        options: buildModelTrimOptions(options?.testDriveVehicles),
      },
      {
        id: 'selectedModelTrimUnavailable',
        fieldType: 'content',
        // Same label and width as the dropdown it replaces, so the row keeps its shape.
        label: labels?.['form.selectedModeltrim'] ?? 'Selected Model/Trim',
        width: 'full',
        color: 'error-1',
        size: 'small',
        content:
          labels?.['form.noVehiclesAvailable'] ??
          'No test drive vehicles are set up for this location.',
        visible: hasNoVehicles(options),
        dependency: { name: 'isTestDrive', value: 'yes' },
      },
      {
        id: 'testDriveDate',
        fieldType: 'date_picker',
        name: 'testDriveDate',
        apiKey: 'testDriveDate',
        label: labels?.['form.date'] ?? 'Date',
        required: true,
        width: 'half',
        size: 'medium',
        minDate: 'today',
        disabled: !options?.selection?.modelTrim,
        dependency: { name: 'isTestDrive', value: 'yes' },
        validationMessages: {
          required: labels?.['form.dateRequired'] ?? 'Date is required',
        },
      },
      {
        id: 'testDriveTime',
        fieldType: 'dropdown',
        name: 'testDriveTime',
        apiKey: 'testDriveTime',
        label: labels?.['form.time'] ?? 'Time',
        required: true,
        placeholder: labels?.['form.timePlaceholder'] ?? 'Select Time',
        width: 'half',
        disabled: !options?.selection?.modelTrim || !options?.selection?.date,
        visible: !hasNoBookableSlots(options),
        dependency: { name: 'isTestDrive', value: 'yes' },
        options: buildTimeOptions(options?.availableSlots),
        validationMessages: {
          required: labels?.['form.timeRequired'] ?? 'Time is required',
        },
      },
      {
        id: 'testDriveTimeUnavailable',
        fieldType: 'content',
        // Same label and width as the dropdown it replaces, so the row keeps its shape.
        label: labels?.['form.time'] ?? 'Time',
        width: 'half',
        color: 'error-1',
        size: 'small',
        content:
          labels?.['form.noSlotsAvailable'] ??
          'No sales consultant is available on this date. Please choose another date.',
        visible: hasNoBookableSlots(options),
        dependency: { name: 'isTestDrive', value: 'yes' },
      },
      {
        id: 'status',
        fieldType: 'dropdown',
        name: 'status',
        apiKey: 'status',
        label: labels?.['form.status'] ?? 'Status',
        placeholder: labels?.['form.statusPlaceholder'] ?? 'Select Status',
        required: true,
        width: 'full',
        dependency: { name: 'isTestDrive', value: 'yes' },
        // A new test drive is booked directly, so it can't start as Requested.
        options: getStatusOptions(labels).filter((option) => option.value !== 'requested'),
        defaultValue: 'scheduled',
        validationMessages: {
          required: labels?.['form.statusRequired'] ?? 'Status is required',
        },
      },
    ],
  },
  {
    id: 'notes',
    fieldType: 'textarea',
    name: 'notes',
    apiKey: 'noteText',
    label: labels?.['form.notes'] ?? 'Notes',
    placeholder: labels?.['form.notesPlaceholder'] ?? 'Enter Notes',
    width: 'full',
  },
  {
    apiKey: 'terms',
    fieldType: 'disclaimers',
    id: 'terms',
    name: 'terms',
    required: false,
    terms:
      labels?.['form.privacyPolicyTerms']?.replace(
        '{{organizationName}}',
        escapeHtml(options?.organizationName || '-')
      ) ??
      `By clicking Create, I confirm that I have informed the prospect that their personal data will be processed by ${escapeHtml(options?.organizationName || '-')}, Lucid Europe B.V., and other relevant Lucid entities (such as local affiliates, importers, and dealers) in accordance with <a href="https://lucidmotors.com/legal#privacy-policy" target="_blank" rel="noopener noreferrer">Lucid's Privacy Policy</a> and, where applicable, the Agent's own privacy policy.`,
  },
  {
    id: 'formButtons',
    fieldType: 'buttonGroup',
    align: 'spaceBetween',
    buttons: [
      {
        id: 'cancelButton',
        label: labels?.['form.cancel'] ?? 'Cancel',
        size: 'medium',
        type: 'secondary',
        color: 'neutral',
        theme: 'dark',
        isFluid: true,
        onClick: options?.onCancel,
      },
      {
        id: 'submitButton',
        label: labels?.['form.create'] ?? 'Create',
        type: 'primary',
        size: 'medium',
        color: 'neutral',
        theme: 'light',
        isFluid: true,
        disabled: isSubmitBlocked(options),
      },
    ],
  },
];
